import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Card, CardBody, Button, Alert } from 'reactstrap';
import { config } from '../../config/environment';
import LoadingState from '../../Components/LoadingState';

const Complete: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [completing, setCompleting] = useState(true);
  const [tenantUrl, setTenantUrl] = useState('');
  const [error, setError] = useState('');

  const completeOnboarding = useCallback(async () => {
    try {
      const flowId = searchParams.get('flow') || searchParams.get('session') || searchParams.get('flowId');
      let onboardingFlowId = flowId || localStorage.getItem('onboardingFlowId');

      if (flowId) {
        localStorage.setItem('onboardingFlowId', flowId);
        onboardingFlowId = flowId;
      }

      if (!onboardingFlowId) {
        setError('No onboarding flow found');
        setCompleting(false);
        return;
      }

      const response = await fetch(config.api.onboardingFlowById(onboardingFlowId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const onboardingFlow = await response.json();

        if (onboardingFlow.status === 'COMPLETE' && onboardingFlow.org) {
          setTenantUrl(`https://${onboardingFlow.org.subdomain}.mentorgain.com`);

          localStorage.removeItem('leadData');
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('onboardingFlowId');
          localStorage.removeItem('initialLeadData');
          localStorage.removeItem('preSelectedPlanId');
        } else {
          setError('Onboarding is not yet complete. Please finish the setup process first.');
        }
      } else {
        setError('Failed to fetch onboarding details');
      }
    } catch (error) {
      setError('Something went wrong while loading your organization details');
    } finally {
      setCompleting(false);
    }
  }, [searchParams]);

  useEffect(() => {
    completeOnboarding();
  }, [completeOnboarding]);

  const redirectToTenant = () => {
    if (tenantUrl) {
      window.location.href = tenantUrl;
    }
  };

  if (completing) {
    return <LoadingState message="Finalizing your setup..." />;
  }

  if (error) {
    return (
      <div className="container py-5">
        <Row className="justify-content-center">
          <Col xs="12" md="6" className="text-center">
            <div className="mb-4">
              <div className="onboarding-icon onboarding-icon--error onboarding-icon--lg">
                <i className="fa-solid fa-triangle-exclamation" />
              </div>
            </div>
            <h4 className="mb-3 fw-bold">Setup Error</h4>
            <p className="text-muted mb-4">{error}</p>
            <Button
              color="primary"
              onClick={() => {
                localStorage.removeItem('onboardingFlowId');
                localStorage.removeItem('leadData');
                localStorage.removeItem('selectedPlan');
                localStorage.removeItem('initialLeadData');
                localStorage.removeItem('preSelectedPlanId');
                window.location.href = '/getting-started';
              }}
            >
              Start Over
            </Button>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-9 col-xl-8">
          <div className="onboarding-card-wrapper">
            <div className="text-center mb-5">
              <div className="mb-4 completion-icon">
                <div className="onboarding-icon onboarding-icon--success onboarding-icon--lg">
                  <i className="fa-solid fa-circle-check" />
                </div>
              </div>
              <h2 className="mb-3 fw-bold">Welcome to Mentorgain!</h2>
              <p className="text-muted lead mb-0">
                Your organization has been successfully set up and is ready to transform
                your mentorship programs.
              </p>
            </div>

            {/* Email Invite Notification */}
            <Alert color="info" className="mb-4 border-0 shadow-sm">
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="fa-solid fa-envelope me-2" style={{ fontSize: '1.25rem' }} />
                <strong>Check Your Email!</strong>
              </div>
              <p className="mb-0 text-center">
                We've sent an admin invitation to your email address. Please check your inbox
                (and spam folder) to complete your admin account setup and access your platform.
              </p>
            </Alert>

            {tenantUrl && (
              <Card className="mb-4 completion-card">
                <CardBody className="text-center p-4">
                  <h5 className="text-primary mb-3 fw-bold">Your Platform is Ready!</h5>
                  <p className="mb-3">Access your personalized Mentorgain platform at:</p>
                  <div className="mb-4 p-3 bg-light rounded-3">
                    <code className="h6 text-dark">{tenantUrl}</code>
                  </div>
                  <Button
                    color="primary"
                    size="lg"
                    onClick={redirectToTenant}
                    className="px-5"
                  >
                    Launch Your Platform <i className="fa-solid fa-arrow-right ms-1" />
                  </Button>
                </CardBody>
              </Card>
            )}

            <Row className="g-3">
              <Col xs="12" md="4">
                <Card className="h-100 completion-card">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="fa-solid fa-circle-check text-success" style={{ fontSize: '2.5rem' }} />
                    </div>
                    <h6 className="mb-2 fw-bold">Payment Complete</h6>
                    <small className="text-muted">
                      Your subscription is active and ready
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col xs="12" md="4">
                <Card className="h-100 completion-card">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="fa-solid fa-circle-check text-success" style={{ fontSize: '2.5rem' }} />
                    </div>
                    <h6 className="mb-2 fw-bold">Organization Created</h6>
                    <small className="text-muted">
                      Your workspace has been configured
                    </small>
                  </CardBody>
                </Card>
              </Col>
              <Col xs="12" md="4">
                <Card className="h-100 completion-card">
                  <CardBody className="text-center py-4">
                    <div className="mb-3">
                      <i className="fa-solid fa-circle-check text-success" style={{ fontSize: '2.5rem' }} />
                    </div>
                    <h6 className="mb-2 fw-bold">Platform Ready</h6>
                    <small className="text-muted">
                      Start building mentorship connections
                    </small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <div className="text-center mt-5 pt-3 border-top">
              <p className="text-muted mb-0">
                Need help getting started? Our support team is here to help at{' '}
                <a href="mailto:support@mentorgain.com" className="text-decoration-none">support@mentorgain.com</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Complete;
